import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { useProducts } from "../../context/ProductContext";
import "./Collection.css";

const defaultBanner = "https://images.pexels.com/photos/7221143/pexels-photo-7221143.jpeg?auto=compress&cs=tinysrgb&w=1920&h=600&fit=crop";

const Collection = () => {
	const { categorySlug } = useParams();
	const { categories, getProductsByCategory, getAllProducts, loading, error } = useProducts();

	const isAll = !categorySlug || categorySlug === "all";
	const products = isAll ? getAllProducts() : getProductsByCategory(categorySlug);
	const category = categories.find((c) => c.slug === categorySlug);
	const title = isAll ? "All Collections" : (category?.label || "Collection");
	const bannerImage = products[0]?.images?.[0] || defaultBanner;

	useEffect(() => {
		window.scrollTo({ top: 0 });
	}, [categorySlug]);

	return (
		<>
			<Helmet>
				<title>{title} - Aire Studio</title>
				<meta name="description" content={`Shop ${title.toLowerCase()} from Aire Studio. Bespoke balloon arrangements and curated gifts for every occasion.`} />
			</Helmet>

			<Navbar />

			<section className="collection-hero">
				<img src={bannerImage} alt={title} className="collection-hero-img" />
				<div className="collection-hero-overlay" />
				<div className="collection-hero-content">
					<motion.h1
						className="collection-hero-title"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
					>
						{title}
					</motion.h1>
				</div>
			</section>

			{!isAll && (
				<nav className="collection-breadcrumb">
					<div className="container">
						<Link to="/">Home</Link>
						<span className="breadcrumb-sep">/</span>
						<Link to="/collections">Shop</Link>
						<span className="breadcrumb-sep">/</span>
						<span className="breadcrumb-current">{title}</span>
					</div>
				</nav>
			)}

			<section className="collection-products section-padding">
				<div className="container">
					{loading ? (
						<div className="collection-loading">
							<div className="collection-spinner" />
							<p>Loading products...</p>
						</div>
					) : error ? (
						<div className="collection-error">
							<p>Unable to load products. Please try again later.</p>
						</div>
					) : products.length === 0 ? (
						<div className="collection-empty">
							<p>No products available yet. Check back soon.</p>
						</div>
					) : (
						<>
							<p className="collection-count">{products.length} products</p>
							<div className="collection-grid">
								{products.map((product, index) => (
									<motion.div
										key={product.id}
										className="product-card"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.5, delay: index * 0.06 }}
									>
										<Link to={`/product/${product.slug}`} className="product-card-link">
											<div className="product-card-image">
												<img src={product.images[0]} alt={product.title} />
												{product.images[1] && (
													<img src={product.images[1]} alt={product.title} className="product-card-hover-img" />
												)}
											</div>
											<div className="product-card-info">
												<h3 className="product-card-title">{product.title}</h3>
												<p className="product-card-price">${product.price.toFixed(2)}</p>
											</div>
										</Link>
									</motion.div>
								))}
							</div>
						</>
					)}
				</div>
			</section>

			<Footer />
		</>
	);
};

export default Collection;
